import { useMutation, useQueryClient } from 'react-query';
import {
  lockTestlet,
  unlockTestlet,
  completeTestletSession,
  startTestletSession,
  resetTestletSessions,
  extendTestletSessionTime,
  logTestletSessionActivity,
  fetchSessionItemsTaken,
  createTestletSession,
} from '@apis/learnosityexams';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import * as SessionAPIs from '@apis/sessions';
import { useUser } from '@reducers/user/hooks';
import {
  AddTestSessionData,
  EditTestSessionData,
  LockStrategy,
  Testlet,
  TestletSession,
} from './types';
import {
  updateCachedTestlet,
  updateCachedTestletSession,
  updateTestletInLearnosityExamTestSessions,
} from './utils/queryHelper';
import { queryKey } from './hooks';

type SessionExamApiResult = {
  response?: unknown;
  error?: string | string[];
};

const unwrapSessionExamResponse = async (
  promise: Promise<SessionExamApiResult>,
  fallbackMessage: string,
) => {
  const result = await promise;
  if (result.error) {
    const message = Array.isArray(result.error) ? result.error[0] : result.error;
    throw new Error(typeof message === 'string' ? message : fallbackMessage);
  }
  return result;
};

export const mutationKey = {
  lockTestlet: (testSessionId: number) => ['lockTestlet', testSessionId],
  unlockTestlet: (testSessionId: number) => ['unlockTestlet', testSessionId],
  completeTestlet: (testletId: number) => ['completeTestlet', testletId],
  startTestlet: (testletId: number) => ['startTestlet', testletId],
  resetTestletSession: (testletId: number) => ['resetTestletSession', testletId],
  editTestSession: () => ['editTestSession'],
  addTestSession: () => ['addTestSession'],
  checkSessionItems: (sessionId: string) => ['checkSessionItems', sessionId],
};

export const useLockTestletMutation = (testSessionId: number) => {
  const { sectionId } = useParams();
  const queryClient = useQueryClient();
  const res = useMutation(
    ({ testletId, lockStrategy }: { testletId: number; lockStrategy: LockStrategy }) =>
      lockTestlet(testletId, { lock_strategy: lockStrategy }),
    {
      mutationKey: ['lockTestlet', testSessionId],
      onSuccess: ({ response }: { response: { data: Testlet } }) => {
        updateCachedTestlet(queryClient, response.data, testSessionId);
        updateTestletInLearnosityExamTestSessions(
          queryClient,
          response.data,
          testSessionId,
          sectionId,
        );
        queryClient.invalidateQueries(['testletSessions', response.data.id]);
      },
      onError: () => {
        toast.error('Failed to lock testlet');
      },
      retry: 3,
      retryDelay: 5 * 1000,
    },
  );
  return res;
};

interface UnlockTestletMutationBody {
  testletId: number;
  body: {
    auto_lock_duration: number;
    lock_strategy: LockStrategy
    update?: boolean;
  };
}

export const useUnlockTestletMutation = (testSessionId: number) => {
  const { sectionId } = useParams();
  const queryClient = useQueryClient();
  const res = useMutation(
    ({ testletId, body }: UnlockTestletMutationBody) => unlockTestlet(testletId, body),
    {
      mutationKey: ['unlockTestlet', testSessionId],
      onSuccess: (
        { response }: { response: { data: Testlet } },
        payload: UnlockTestletMutationBody,
      ) => {
        updateCachedTestlet(queryClient, response.data, +testSessionId);
        updateTestletInLearnosityExamTestSessions(
          queryClient,
          response.data,
          testSessionId,
          sectionId,
        );
        queryClient.invalidateQueries(['testletSessions', +response.data.id]);
        if (payload.body?.update) {
          toast.success('Testlet updated successfully');
        }
      },
      onError: () => {
        toast.error('Failed to unlock testlet');
      },
      retry: 3,
      retryDelay: 5 * 1000,
    },
  );
  return res;
};

export const useCompleteTestletSessionMutation = (testletId: number) => {
  const { sectionId } = useParams();
  const queryClient = useQueryClient();
  const res = useMutation(() => completeTestletSession(testletId), {
    mutationKey: ['completeTestlet', testletId],
    onSuccess: ({ response }: { response: { data: TestletSession } }) => {
      toast.success('Test submitted');
      updateCachedTestletSession(queryClient, response.data, testletId);
      queryClient.invalidateQueries(['testSessionCompletionStatus', +sectionId]);
    },
    onError: () => {
      toast.error('Failed to submit test');
    },
  });
  return res;
};

export const useStartTestletSessionMutation = (testletId: number) => {
  const queryClient = useQueryClient();
  const res = useMutation(() => startTestletSession(testletId), {
    mutationKey: ['startTestlet', testletId],
    onSuccess: ({ response }: { response: { data: TestletSession } }) => {
      updateCachedTestletSession(queryClient, response.data, testletId);
    },
  });
  return res;
};

export const useCreateTestletSessionMutation = (testletId) => {
  const currentUser = useUser();
  const queryClient = useQueryClient();
  const res = useMutation(() => createTestletSession(testletId, currentUser.id), {
    mutationKey: ['createTestletSession', testletId, currentUser.id],
    onSuccess: ({ response }: { response: { data: TestletSession[] } }) => {
      updateCachedTestletSession(queryClient, response.data, testletId);
      queryClient.invalidateQueries(['testletSessions']);
    },
    onError: () => {
      toast.error('Failed to start testlet session');
    },
  });
  return res;
};

export const useResetTestletSessionMutation = (testletId: number) => {
  const queryClient = useQueryClient();
  const res = useMutation(
    (studentIds: number[]) => resetTestletSessions(testletId, studentIds),
    {
      mutationKey: ['resetTestletSession', testletId],
      onSuccess: ({ response }: { response: { data: TestletSession[] | any } }) => {
        toast.success('Testlet session reset successfully');
        updateCachedTestletSession(queryClient, response.data, testletId);
        // todo: remove this once we make sure that the invalidation works correctly
        // for demo's sake
        queryClient.invalidateQueries(['testletSessions']);
      },
      onError: () => {
        toast.error('Failed to reset testlet session');
      },
    },
  );
  return res;
};

export const useExtendTestletSessionTimeMutation = (testletId: number) => {
  const queryClient = useQueryClient();
  const res = useMutation(
    ({ studentIds, extendDuration }: { studentIds: number[]; extendDuration: number }) =>
      extendTestletSessionTime(testletId, studentIds, extendDuration),
    {
      mutationKey: ['extendTestletSessionTime', testletId],
      onSuccess: ({ response }: { response: { data: TestletSession[] } }) => {
        toast.success('Testlet session time extended successfully');
        updateCachedTestletSession(queryClient, response.data, testletId);
      },
      onError: () => {
        toast.error('Failed to extend testlet session time');
      },
    },
  );
  return res;
};

export const useLogTestletSessionActivityMutation = (testletSessionId: string) => {
  const res = useMutation(
    ({ eventType, metadata = {} }: { eventType: string; metadata?: any }) =>
      logTestletSessionActivity(testletSessionId, eventType, metadata),
    {
      mutationKey: ['logTestletSessionActivity', testletSessionId],
    },
  );
  return res;
};
export const useEditTestSessionMutation = () => {
  const queryClient = useQueryClient();
  const res = useMutation({
    mutationKey: mutationKey.editTestSession(),
    mutationFn: (data: EditTestSessionData) =>
      unwrapSessionExamResponse(
        SessionAPIs.updateSessionExam(data.id, data),
        'Failed to edit test session',
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(
        queryKey.getLearnosityExamTestSessions({
          sectionId: variables.section,
        }),
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to edit test session');
    },
  });
  return res;
};
export const useAddTestSessionMutation = () => {
  const queryClient = useQueryClient();
  const res = useMutation({
    mutationKey: mutationKey.addTestSession(),
    mutationFn: (data: AddTestSessionData) =>
      unwrapSessionExamResponse(
        SessionAPIs.addSessionExam(data),
        'Failed to add test session',
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKey.getLearnosityExamTestSessions({
          sectionId: variables.section,
        }),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add test session');
    },
  });
  return res;
};

export const useCheckSessionItemsTaken = (sessionId: string) => {
  const res = useMutation(
    (testletSessionId: number) => fetchSessionItemsTaken(testletSessionId),
    {
      mutationKey: mutationKey.checkSessionItems(sessionId),
    },
  );
  return res;
};
